import { kMedChronApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { throwIfEditingLeaseDenial } from '@/editing-leases/schemas'
import { readResponseJson, getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { ApiError } from '@/util/errors/api-error'
import { InitializeChronologyRequest, InitializeChronologyResponse, InitializeChronologyResponseSchema } from '../store/schemas'

/**
 * Creates the matter's living chronology from an initial file selection and enqueues
 * its first extraction run. Throws ApiError(409) when the matter already has one.
 * Typed editing-lease denials also throw ApiError(409) with their structured detail.
 */
export async function postInitializeChronology(payload: InitializeChronologyRequest): Promise<InitializeChronologyResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify(payload),
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMedChronApiBasePath}/asset`
    const response = await fetch(apiUrl, requestOptions)

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
      throwIfEditingLeaseDenial(response.status, detail)
      Sentry.captureException(new Error(`Chronology submission failed: ${response.status}`), {
        tags: { transaction_id: sentry_transaction_id },
        extra: getResponseDebugContext(response, apiUrl),
      })
      throw new ApiError(response.status, 'Could not submit the chronology change', detail)
    }
    const parsed = InitializeChronologyResponseSchema.safeParse(data)
    if (parseError || !parsed.success) {
      Sentry.captureException(new Error('Invalid chronology submission response'), {
        tags: { transaction_id: sentry_transaction_id },
        extra: { ...getResponseDebugContext(response, apiUrl), responseKeys: getTopLevelKeys(data) },
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid chronology submission response')
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.captureException(error, {
        tags: { transaction_id: sentry_transaction_id },
        extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
      })
    }
    throw error
  }
}
